<template>
  <div class="font-roboto">

    <!-- Push Notification Banner -->
    <div
      v-if="showNotificationBanner && !isWrapped"
      class="sticky top-0 z-40 bg-blue-600 text-white text-center flex items-center flex-wrap"
    >
      <div class="flex-1 flex items-center justify-center gap-3 py-3 px-4">
        <span class="text-sm sm:text-base"
          >Enable push notifications to receive building alerts on this device</span
        >
        <BaseButton
          class="bg-white text-blue-600 hover:bg-blue-100 font-bold text-sm"
          @click="openEnablePopup()"
          >Enable</BaseButton
        >
      </div>
      <button
        class="text-white/70 hover:text-white text-2xl leading-none px-4 py-3 self-stretch"
        @click="askMeNextTime = true"
        title="Dismiss"
      >
        &times;
      </button>
    </div>

    <!-- Notification Active indicator (footer-style, for test users) -->
    <!-- DISABLED: currently decided to only use the banner for notifications, 
            but can re-enable this if we want a persistent indicator for test users -->
    <div
      v-if="notificationsEnabled && isTestUser && !isWrapped && false"
      class="text-center py-1 bg-green-50 text-green-700 text-xs border-b border-green-200 cursor-pointer hover:bg-green-100"
      @click="openDisablePopup()"
    >
      Push Notifications Active
    </div>



    <SlimHeader
      v-if="!isWrapped"
      :title="layoutTitle"
      :enable-building-switcher="enableBuildingSwitcher"
      @open-building-switcher="buildingSwitcherVisible = true"
    />



    <!-- Incoming Push Notification Popup -->
    <BasePopup
      title="Notification"
      :is-visible="!!incomingMessage"
      :is-full-width="false"
      @close="dismissNotification"
    >
      <div class="p-4">
        <p class="font-bold text-lg mb-2">{{ incomingMessage?.title }}</p>
        <p class="text-sm text-gray-700">{{ incomingMessage?.body }}</p>
        <div class="mt-4 flex justify-end">
          <BaseButton @click="dismissNotification">OK</BaseButton>
        </div>
      </div>
    </BasePopup>

    <!-- Enable Notifications Popup -->
    <BasePopup
      title="Enable Alert Push Notifications"
      :is-visible="isEnableNotificationsPopupVisible"
      :is-full-width="false"
      @close="isEnableNotificationsPopupVisible = false"
    >
      <div v-if="notificationsBlocked" id="unblock-me-info">
        <div class="text-xl font-bold mb-4">Notification Permission Blocked</div>
        <p class="mb-4">
          Oops, it looks like notifications have been set to blocked for this site. This
          could be because you clicked "Block" on the permission popup, or because your
          browser is blocking notifications by default.
        </p>
        <p class="mb-4">Here is how to unblock notifications for this site:</p>
        <ol class="list-decimal ml-5 pl-4">
          <li>Open your browser settings</li>
          <li>Look for "Notifications" or "Site Settings"</li>
          <li>Find this site and change the notification permission to "Allow"</li>
          <li>Click "Yes" below to try again</li>
        </ol>

        <div v-if="isEdge">
          <img
            src="/images/Enable_Notifications_Edge_Desktop.jpg"
            alt="Edge notification settings example"
            class="my-4 border"
          />
        </div>
        <div v-if="isChrome">
          <img
            src="/images/Enable_Notifications_Chrome_Desktop1.jpg"
            alt="Chrome notification settings example"
            class="my-4 border"
          />
          <img
            src="/images/Enable_Notifications_Chrome_Desktop2.jpg"
            alt="Chrome notification settings example"
            class="my-4 border"
          />
        </div>
        <div v-if="isFirefox">
          <img
            src="/images/Enable_Notifications_Firefox_Desktop1.jpg"
            alt="Firefox notification settings example"
            class="my-4 border"
          />
          <img
            src="/images/Enable_Notifications_Firefox_Desktop2.jpg"
            alt="Firefox notification settings example"
            class="my-4 border"
          />
          <img
            src="/images/Enable_Notifications_Firefox_Desktop3.jpg"
            alt="Firefox notification settings example"
            class="my-4 border"
          />
        </div>
      </div>
      <div v-else id="enable-notifications-info" class="p-4">
        <div v-if="notificationsError">
          <p class="mb-4 text-red-500">
            An error occurred while subscribing to notifications, please reload and try
            again.
          </p>
          <p class="text-sm text-gray-700 italic">
            {{ notificationsError }}
          </p>
        </div>
        <p class="">
          Your building sends important life safety information using the SafetyNet Alert
          system. By enabling push notifications, you'll receive these Alerts in real time
          on this device.
        </p>
      </div>
      <div class="mb-8 max-w-96 flex flex-col items-center justify-center mx-auto">
        <img
          src="/images/desktop_notification_example.jpg"
          alt="Desktop notification example"
          class="my-4 border"
        />
        <img
          src="/images/mobile_notification_example.jpg"
          alt="Mobile notification example"
          class="my-4 border"
        />
      </div>
      <div>Would you like to receive Building Alerts on this device?</div>
      <div id="buttons">
        <div>
          <Loading v-if="attemptingNotificationsOperation" class="mx-auto mb-4" />
        </div>
        <div class="mt-4 pt-4 flex gap-2 justify-end">
          <BaseButton
            class="bg-blue-400 hover:bg-blue-900 min-w-24 font-bold"
            @click="handleOptIn()"
            >Yes</BaseButton
          >
          <BaseButton @click="handleOptOutThisDevice()">Not on this device</BaseButton>
          <BaseButton
            @click="
              askMeNextTime = true;
              isEnableNotificationsPopupVisible = false;
              attemptingNotificationsOperation = false;
            "
            >Ask me later</BaseButton
          >
        </div>
        <div v-if="!isConfirmingOptOutAll">
          <a
            class="float-end ml-2 mt-4 underline text-blue-800 cursor-pointer"
            @click="isConfirmingOptOutAll = true"
            >Opt out everywhere</a
          >
        </div>
        <div v-else class="mt-4 p-3 bg-red-50 border border-red-200 rounded">
          <p class="text-sm text-red-800 mb-2">
            This will disable push notifications on <strong>all</strong> your devices. Are
            you sure?
          </p>
          <div class="flex gap-2 justify-end">
            <BaseButton
              class="bg-red-500 hover:bg-red-700 text-white"
              @click="handleOptOutAllDevices()"
              >Yes, opt out everywhere</BaseButton
            >
            <BaseButton @click="isConfirmingOptOutAll = false">Cancel</BaseButton>
          </div>
        </div>
      </div>
      <div class="text-xs text-gray-700 mt-4">
        <span v-if="isSafari">Safari </span>
        <span v-if="isEdge">Edge </span>
        <span v-if="isChrome">Chrome </span>
        <span v-if="isFirefox">Firefox </span>
        <span v-if="!isEdge && !isSafari && !isChrome && !isFirefox"
          >Browser: '{{ browserName }}'</span
        >
        <span v-if="isiOS17plus"> on iOS 17+ </span>
        <span v-if="isIOS && !isiOS17plus"> on iOS </span>
        <span v-if="isMacOS"> on MacOS </span>
        <span v-if="isWindows"> on Windows </span>
        <span v-if="isAndroid"> on Android </span>
        <span v-if="isStandalone"> App </span>
      </div>
    </BasePopup>

    <!-- Disable Notifications Popup -->
    <BasePopup
      title="Alert Push Notifications"
      :is-visible="isDisableNotificationsPopupVisible"
      :is-full-width="false"
      @close="isDisableNotificationsPopupVisible = false"
    >
      <div v-if="notificationsError">
        <p class="mb-4 text-red-500">
          An error occurred while unsubscribing from notifications, please reload and try
          again.
        </p>
        <p class="text-sm text-gray-700 italic">
          {{ notificationsError }}
        </p>
      </div>
      <div class="p-4">
        <p class="mb-4">
          Your building sends important life safety information using the SafetyNet Alert
          system. To unregister this device from receiving these Alerts using push
          notifications, click the <em>Disable</em> button below.
        </p>

        <div>
          <Loading v-if="attemptingNotificationsOperation" class="mx-auto mb-4" />
        </div>
        <div class="mt-4 pt-4 flex justify-end">
          <BaseButton @click="unsubscribeFromNotifications()">Disable</BaseButton>
          <BaseButton
            class="ml-2"
            @click="
              isDisableNotificationsPopupVisible = false;
              attemptingNotificationsOperation = false;
            "
            >Close</BaseButton
          >
        </div>
        <div class="text-xs text-gray-700 mt-4">
          <span v-if="isSafari">Safari </span>
          <span v-if="isEdge">Edge </span>
          <span v-if="isChrome">Chrome </span>
          <span v-if="isFirefox">Firefox </span>
          <span v-if="!isEdge && !isSafari && !isChrome && !isFirefox"
            >Browser: '{{ browserName }}'</span
          >
          <span v-if="isiOS17plus"> on iOS 17+ </span>
          <span v-if="isIOS && !isiOS17plus"> on iOS </span>
          <span v-if="isMacOS"> on MacOS </span>
          <span v-if="isWindows"> on Windows </span>
          <span v-if="isAndroid"> on Android </span>
          <span v-if="isStandalone"> App </span>
        </div>
      </div>
    </BasePopup>

    <!-- iOS Not-Installed popup -->
    <BasePopup
      id="not-installed-popup"
      title="Enable Alert Push Notifications"
      :is-visible="isIOSNotInstalledPopupVisible"
      :is-full-width="false"
      @close="isIOSNotInstalledPopupVisible = false"
    >
      <div class="px-4">
        <p class="font-bold text-lg">Add to Home Screen</p>
        <p class="text-md mb-2">
          Add SafetyNet to your home screen to enable Push notifications
        </p>
        <p class="text-md mb-2">
          Click the share menu in Safari and select "Add to Home Screen", then open the
          app from your home screen.
        </p>
        <img
          src="/images/iphone_install1.jpg"
          alt="click share icon"
          class="mr-12 my-6 border-2"
        />
        <img
          src="/images/iphone_install2.jpg"
          alt="click add to home screen"
          class="mr-12 my-6 border-2"
        />
        <img
          src="/images/iphone_install3.jpg"
          alt="click add button"
          class="mr-12 my-6 border-2"
        />
        <div class="mt-4 flex justify-end">
          <BaseButton @click="isIOSNotInstalledPopupVisible = false">OK</BaseButton>
        </div>
      </div>
    </BasePopup>

    <div
      class="px-5 text-sm lg:text-sectionHdr py-10"
      :class="[isWrapped ? 'layout-wrapped' : 'lg:px-12 2xl:px-64']"
    >
      <div class="container mx-auto" v-if="property?.isActive || !hasBuilding">
        <!-- DEBUG>BethsLayout>Before Slot -->
        <slot></slot>
        <!-- DEBUG>BethsLayout>After Slot --->
      </div>
      <div v-else-if="isLoading">
        <Loading />
      </div>
      <div v-else-if="loadFailed">
        <h1>Something went wrong</h1>
        <p>We couldn't load this site. Please check your connection and try again.</p>
        <BaseButton class="mt-4" @click="retryLoad">Try again</BaseButton>
      </div>
      <div v-else>
        <h1>Not Found</h1>
        <p>Please check your link and try again</p>
      </div>
    </div>
    <TrainingFooter
      v-if="property && !isWrapped"
      :enable-building-switcher="enableBuildingSwitcher"
      :notifications-enabled="notificationsEnabled"
      :on-enable-notifications="openEnablePopup"
      :on-disable-notifications="openDisablePopup"
      @open-building-switcher="buildingSwitcherVisible = true"
    />
  </div>
  <BasePopup
    :isVisible="buildingSwitcherVisible"
    title="Building Switcher"
    :is-full-width="false"
    @close="buildingSwitcherVisible = false"
  >
    <div v-if="portalAccessList.length === 0">No additional available portals.</div>
    <div v-else>
      <a
        v-for="portal in portalAccessList"
        @click="switchBuilding(portal)"
        :title="`/${portal.siteCode}?login=true`"
        class="w-full text-btn font-normal bg-gray-100 hover:bg-blue-200 py-2 pl-4 mb-2 border border-gray-400 rounded-t-sm drop-shadow hover:drop-shadow-xxl flex"
      >
        <i-heroicons-outline-check
          class="w-6 h-6 mr-2 m-auto"
          :class="property?.siteCode === portal.siteCode ? '' : 'invisible'"
        />
        <div class="text-lg flex-grow">
          <span v-if="portal.category">{{ portal.category }} ></span> {{ portal.name }}
        </div>
      </a>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";
import router from "../router";
import { PortalListDTO } from "../store/generatedFetchClient";
import Loading from "../components/Loading.vue";
import { usePushNotifications } from "../composables/usePushNotifications";
import { userStorageKeyFor, safeStorageGet, safeStorageSet } from "../helpers";
import { TrackJS } from "trackjs";

const {
  notificationsEnabled,
  showNotificationBanner,
  isEnableNotificationsPopupVisible,
  isDisableNotificationsPopupVisible,
  isIOSNotInstalledPopupVisible,
  attemptingNotificationsOperation,
  notificationsError,
  notificationsBlocked,
  isConfirmingOptOutAll,
  incomingMessage,
  isTestUser,
  browserName,
  isIOS,
  isAndroid,
  isMacOS,
  isWindows,
  isStandalone,
  isSafari,
  isEdge,
  isChrome,
  isFirefox,
  isiOS17plus,
  dismissNotification,
  handleOptIn,
  handleOptOutThisDevice,
  handleOptOutAllDevices,
  unsubscribeFromNotifications,
  openEnablePopup,
  openDisablePopup,
  askMeNextTime
} = usePushNotifications();

var props = withDefaults(
  defineProps<{
    layoutTitle?: string;
    isWrapped?: boolean;
    hasBuilding?: boolean; // set to false when page loads without a building
  }>(),
  {
    layoutTitle: "SafetyNet",
    isWrapped: false,
    hasBuilding: true,
  }
);

var bldgStore = useBldgStore();
var property = computed(() => bldgStore.property);
var userStore = useUserStore();
var user = computed(() => userStore.user);
var isLoading = computed(() => bldgStore.isLoading);
// A non-404 failure (network, 5xx). A 404 is a genuinely unknown site code and still
// shows the "Not Found" message below.
var loadFailed = computed(
  () => bldgStore.loadError !== null && bldgStore.loadError.status !== 404
);

async function retryLoad() {
  if (bldgStore.loadError) {
    await bldgStore.tryGetBuildingData(bldgStore.loadError.siteCode, true);
  }
}

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  if (!props.hasBuilding) return;
  let forceLogin = location.search.indexOf("login") >= 0;

  if (property?.value?.portalId) {
    try {
      await userStore.getUser(
        property.value.siteCode!,
        property.value.portalId!,
        forceLogin,
        forceLogin ? property.value.clientSystemName! : ""
      );

      if (forceLogin) {
        // Clear ?login=(usertoken) to avoid re-force reload
        router.replace(location.pathname);
      }

      // If user loaded with siteCodeAlias in url, update url to use siteCode instead
      var urlSiteCode = router?.currentRoute?.value?.params["sitecode"] as string;
      if (urlSiteCode?.length > 0 && urlSiteCode?.toLowerCase() === property.value?.siteCodeAlias?.toLowerCase()) {
        console.log(
          `Loaded on siteCodeAlias of ${urlSiteCode} updating to use ${property.value.siteCode} `
        );
        var address = router?.currentRoute?.value?.fullPath;
        address = address.replace("/" + urlSiteCode, "/" + property.value?.siteCode!);
        router.replace(address);
      }

      //console.log("BethsLayout> User load complete", user.value);
    } catch (e: any) {
      // Was silently swallowed with a commented-out body, which hid every failure in
      // here - including storage errors thrown by getUser in embedded WebViews.
      console.warn("BethsLayout> Error loading user", e);
      if ((import.meta as any).env.PROD) {
        TrackJS.track(e);
      }
    }

    // Load portals for building switcher if applicable
    if (userStore.isLoggedIn) {
      if (
        user.value?.grantedAccessToClient ||
        property.value?.clientEnableBuildingPickerForAllUsers
      )
        await bldgStore.getPortalList(property.value.clientSystemName!);
      else if (user.value?.grantedAccessToRegion)
        await bldgStore.getPortalList(
          property.value.clientSystemName!,
          user.value.grantedAccessToRegion!
        );
    }
  }
}

// #region Building switcher 🏢
var portalAccessList = computed(() => bldgStore.portalList);
var buildingSwitcherVisible = ref(false);

var enableBuildingSwitcher = computed(
  () =>
    userStore.isLoggedIn &&
    (user.value?.grantedAccessToClient ||
      user.value?.grantedAccessToRegion != "" ||
      property.value?.clientEnableBuildingPickerForAllUsers) &&
    portalAccessList.value?.length > 0
);

function switchBuilding(targetPortal: PortalListDTO) {
  // If user doesn't exist for new portal, write a temp one for new portal to pickup.
  // Read and write the same canonical key - these previously disagreed on casing, so a
  // mixed-case portal missed its real cached user and overwrote it with the placeholder.
  let targetPortalUserKey = userStorageKeyFor(targetPortal.siteCode);
  let targetPortalUser = safeStorageGet(targetPortalUserKey);
  if (!targetPortalUser) {
    safeStorageSet(
      targetPortalUserKey,
      JSON.stringify({
        note:
          "This is a placeholder object until user can be reloaded in context of this new portal",
        personId: user.value!.personId,
        loginEmail: user.value!.loginEmail,
        token: user.value!.token,
      })
    );
  }

  //redirect user to new portal
  location.assign(`/${targetPortal.siteCode}?login=true`);
}

// #endregion Building switcher 🏢
</script>
